import { FC, useState } from 'react';
import {
  useGetTodayScheduledCallsQuery,
  useGetScheduledCallsQuery,
  useGetUserProjectsQuery,
  useGetCourseHearingQuery,
  useGetCurrentUserQuery,
  UserProjectStatus,
  ScheduledCall,
} from '@teachur/http-client';
import { Loader } from '@new/common/ui/Loader/Loader';
import { useVideoCallContext } from '../../../../contexts/VideoCallProvider';
import { useRouterHooks } from '../../../../utils/routerHooks';
import { StatsCards } from '../components/StatsCards/StatsCards';
import { CalendarAgenda } from '../components/CalendarAgenda/CalendarAgenda';
import { TodayAgenda } from '../components/TodayAgenda/TodayAgenda';
import { ActionRequired } from '../components/ActionRequired/ActionRequired';
import { ProjectsDrawer } from '../../projects-review/components/ProjectsDrawer/ProjectsDrawer';
import { CourseHearingDrawer } from '../../course-hearings/components/CourseHearingDrawer/CourseHearingDrawer';
import { User } from '../types';
import { CALLS_PAGE_SIZE, PROJECTS_PAGE_SIZE } from '../constants';
import { PageHeader } from '@new/common/ui/PageHeader/PageHeader';
import { formatRoleLabel } from '../lib/helpers';

interface InstructorDashboardProps {
  user: User;
}

export const InstructorDashboard: FC<InstructorDashboardProps> = ({ user }) => {
  const { openVideoCall } = useVideoCallContext();
  const { history } = useRouterHooks();
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);
  const [selectedHearingId, setSelectedHearingId] = useState<number | null>(null);
  const [selectedDate, setSelectedDate] = useState(new Date());

  const { data: currentUser, isLoading: isCurrentUserLoading } = useGetCurrentUserQuery();

  const { data: selectedHearing } = useGetCourseHearingQuery(selectedHearingId ?? undefined, {
    enabled: !!selectedHearingId,
  });

  const onSelectCall = (call: ScheduledCall) => {
    if (call.courseHearingId) {
      setSelectedHearingId(call.courseHearingId);
    } else if (call.videoCallId) {
      openVideoCall(call.videoCallId);
    }
  };

  const instructorId = currentUser?.id;

  const hasInstructor = !!instructorId;

  const { data: todayData, isLoading: isTodayLoading } = useGetTodayScheduledCallsQuery();
  const { data: acceptedData, isLoading: isAcceptedLoading } = useGetScheduledCallsQuery(
    { status: 'accepted', instructorId, page: 1, pageSize: CALLS_PAGE_SIZE },
    { enabled: hasInstructor }
  );
  const { data: completedData, isLoading: isCompletedLoading } = useGetScheduledCallsQuery(
    { status: 'completed', instructorId, page: 1, pageSize: 1 },
    { enabled: hasInstructor }
  );
  const { data: projectsData, isLoading: isProjectsLoading } = useGetUserProjectsQuery({
    status: UserProjectStatus.Pending,
    page: 1,
    pageSize: PROJECTS_PAGE_SIZE,
  });
  const { data: allProjectsData } = useGetUserProjectsQuery({ page: 1, pageSize: CALLS_PAGE_SIZE });

  const todayCalls = todayData?.data ?? [];
  const acceptedCalls = acceptedData?.data ?? [];
  const completedHearingsCount = completedData?.total ?? 0;
  const pendingProjects = projectsData?.data ?? [];
  const allProjects = allProjectsData?.data ?? [];

  const isLoading =
    isCurrentUserLoading ||
    isTodayLoading ||
    isProjectsLoading ||
    (hasInstructor && (isAcceptedLoading || isCompletedLoading));

  const completedProjects = allProjects.filter((project) => project.status === 'approved').length;

  const selectedProject = allProjects.find((project) => project.id === selectedProjectId) ?? null;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader />
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col gap-8 p-6 lg:p-8">
        <div className="flex flex-col flex-1 w-full gap-4">
          <PageHeader
            title={`Welcome back, ${formatRoleLabel(user?.role)} ${user?.lastName}`}
            description="Here's what's happening with your project reviews and scheduled calls."
          />

          <div className="flex flex-col gap-4 xl:flex-row">
            <div className="flex min-w-0 flex-1 flex-col gap-4">
              <StatsCards
                pendingHearings={todayCalls.length}
                pendingProjects={pendingProjects.length}
                completedHearings={completedHearingsCount}
                completedProjects={completedProjects}
                onNavigate={history.push}
              />

              <div className="flex flex-col gap-4 lg:flex-row">
                <TodayAgenda
                  onSelectProject={(project) => setSelectedProjectId(project?.id ?? null)}
                  onSelectCall={onSelectCall}
                />
                <ActionRequired
                  onSelectProject={(project) => setSelectedProjectId(project?.id ?? null)}
                  onSelectCall={onSelectCall}
                />
              </div>
            </div>

            <div className="flex w-full xl:w-auto">
              <CalendarAgenda
                scheduledCalls={acceptedCalls}
                selectedDate={selectedDate}
                onDateChange={setSelectedDate}
                onJoinCall={openVideoCall}
              />
            </div>
          </div>
        </div>
      </div>

      <ProjectsDrawer
        selectedProject={selectedProject}
        setSelectedProjectId={setSelectedProjectId}
      />

      <CourseHearingDrawer
        selectedCall={selectedHearing ?? null}
        setSelectedCall={() => setSelectedHearingId(null)}
      />
    </>
  );
};
